import Col from 'antd/es/col'
import Form from 'antd/es/form'
import Input from 'antd/es/input'
import InputNumber from 'antd/es/input-number'
import message from 'antd/es/message'
import notification from 'antd/es/notification'
import Row from 'antd/es/row'
import Segmented from 'antd/es/segmented'
import Select from 'antd/es/select'
import TreeSelect from 'antd/es/tree-select'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import {
  selectTheme,
  selectThemeStyle,
} from '../../../../../features/setting/setting.slice.ts'
import { convertPxToNumber } from '../../../../../utils/helpers.ts'
import IdeyaTypography from '../../../../../common/antd/ideya-typography.tsx'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import styles from './action-idea-form.module.css'
import { IdeyaButton } from '../../../../../common/antd/ideya-button.tsx'
import { Checkbox, FormInstance, FormProps } from 'antd/lib/index'
import Radio from 'antd/es/radio/index'
import {
  useGetTickerShortableQuery,
  useLazyGetTickerPriceQuery,
  useLazySearchTickerQuery,
} from '../../../../../apis/ticker/ticker-api.api.ts'
import { debounce } from 'lodash'
import {
  FillStrategies,
  TickerOption,
  TradeReasonsTreeData,
} from './action-idea-form.ts'
import { UserInfo } from '../../../../../apis/auth/auth.def.ts'
import {
  useCheckFillStrategyQuery,
  useCheckIdeaExistQuery,
  useCreateIdeaMutation,
  useUpdateIdeaMutation,
} from '../../../../../apis/idea/idea-api.api.ts'
import { getErrorDescription } from '../../../../../redux/utils.ts'
import { IdeaDetail } from '../../../../../apis/idea/idea-api.def.ts'
import { RuleObject } from 'antd/es/form/index'
import { useIsMobile } from '../../../../../common/hooks/use-is-mobile.ts'
import {
  Ticker,
  TickerSearchResponse,
} from '../../../../../apis/ticker/ticker-api.def.ts'
import { useTranslation } from 'react-i18next'
import { InvalidTickerMessage } from './invalid-ticker-message.tsx'
import { Theme } from '../../../../../features/setting/setting.def.ts'
import { skipToken } from '@reduxjs/toolkit/query'

// Transition MS Antd Segmented Thumb (Animation on changed selection)
const THUMB_TRANSITION_MS = 200

export type CreateIdeaFormFieldTypes = {
  figi: string
  direction: 'Long' | 'Short'
  targetMarketValue: number
  target: number
  stop: number
  fillStrategy: string
  tradeReason: string
  horizon: string
  conviction: string
  comment: string
}

interface CreateIdeaFormProps {
  onCancel: () => void
  form: FormInstance<CreateIdeaFormFieldTypes>
  buyingPower?: number
  usedBuyingPower?: number
  userInfo: UserInfo
  onIdeaActioned?: () => void
  mode?: 'create' | 'edit'
  ideaDetail?: IdeaDetail
  onEditIdea?: (ideaId: string) => void
  onIdeaLiquidated: () => void
}

export interface CreateIdeaFormRef {
  resetFormState: () => void
}

export const ActionIdeaForm = forwardRef<
  CreateIdeaFormRef,
  CreateIdeaFormProps
>(
  (
    {
      onCancel,
      form,
      buyingPower,
      usedBuyingPower,
      userInfo,
      onIdeaActioned,
      ideaDetail,
      mode = 'create',
      onEditIdea,
      onIdeaLiquidated,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const themeStyle = useAppSelector(selectThemeStyle)
    const theme = useAppSelector(selectTheme)
    const [visualDirection, setVisualDirection] = useState<'Long' | 'Short'>(
      'Long'
    )
    const [isMaxFillReached, setIsMaxFillReached] = useState<boolean>(false)
    const [horizonValue, setHorizonValue] = useState<string>(
      ideaDetail?.horizon ?? ''
    )
    const [convictionValue, setConvictionValue] = useState<string>(
      ideaDetail?.conviction ?? ''
    )
    const [tickerOptions, setTickerOptions] = useState<TickerOption[]>([])
    const [companyName, setCompanyName] = useState<string>('')
    const [maxTargetMarketValue, setMaxTargetMarketValue] =
      useState<number>(5000000)
    const [selectedBbgCompositeKey, setSelectedBbgCompositeKey] =
      useState<string>(ideaDetail?.bbgCompositeKey ?? '')
    const [selectedFigi, setSelectedFigi] = useState<string>('')
    // For mobile only
    const [selectedTradeReasons, setSelectedTradeReasons] = useState<string[]>(
      []
    )
    const [messageApi, messageContextHolder] = message.useMessage()
    const [notificationApi, notificationContextHolder] =
      notification.useNotification()
    const MinTargetMarketValue = 600000
    const TargetMarketValueStep = 100000
    const availableCapital = (
      (buyingPower ?? 0) - (usedBuyingPower ?? 0)
    ).toLocaleString(undefined, { maximumFractionDigits: 0 })

    // Special check for JP and Global users (Different BP value buttons)
    const isJpOrGlobal =
      userInfo.region === 'Japan' || userInfo.region === 'Global'

    const { data: fillStrategyData, isLoading: fillStrategyLoading } =
      useCheckFillStrategyQuery(
        ideaDetail
          ? {
              id: ideaDetail.id,
              fillStrategy: ideaDetail.fillStrategy,
            }
          : skipToken,
        {
          refetchOnMountOrArgChange: true,
          skip: !ideaDetail,
        }
      )

    const [fetchTickerSearch, { data: tickerData, isLoading: tickerLoading }] =
      useLazySearchTickerQuery()

    const [fetchTickerPrice, { data: tickerPriceData }] =
      useLazyGetTickerPriceQuery()

    const [
      createIdea,
      { isLoading: isCreating, isSuccess, isError, error, reset },
    ] = useCreateIdeaMutation()

    const [
      updateIdea,
      {
        isLoading: isUpdateLoading,
        isSuccess: isUpdateSuccess,
        isError: isUpdateError,
        error: updateError,
        reset: updateReset,
      },
    ] = useUpdateIdeaMutation()

    const { data: isShortableData, isLoading: isShortableLoading } =
      useGetTickerShortableQuery(
        { bbgCompositeKey: selectedBbgCompositeKey },
        {
          skip: !selectedBbgCompositeKey,
          refetchOnMountOrArgChange: true,
          refetchOnReconnect: true,
        }
      )

    const {
      currentData: checkIdeaExistData,
      isLoading: checkIdeaExistLoading,
    } = useCheckIdeaExistQuery({ figi: selectedFigi }, { skip: !selectedFigi })

    useEffect(() => {
      if (ideaDetail) {
        const tickerOption: TickerOption = {
          label: ideaDetail.bbgCompositeKey,
          bbgCompositeKey: ideaDetail.bbgCompositeKey,
          name: ideaDetail.ticker.name,
          legalName: ideaDetail.ticker.legalName,
          value: ideaDetail.ticker.figi,
          status: ideaDetail.ticker.status,
        }

        fetchTickerSearch({ keyword: ideaDetail.ticker.figi })
        fetchTickerPrice({ bbgCompositeKey: ideaDetail.ticker.bbgCompositeKey })
        setSelectedBbgCompositeKey(ideaDetail.ticker.bbgCompositeKey)

        setTickerOptions([tickerOption])
        let finalCompanyName = ideaDetail.ticker.name
        if (ideaDetail.ticker.legalName) {
          finalCompanyName = finalCompanyName.concat(
            ' (' + ideaDetail.ticker.legalName + ')'
          )
        }
        setCompanyName(finalCompanyName)
        setHorizonValue(ideaDetail.horizon)
        setConvictionValue(ideaDetail.conviction)
        setVisualDirection(ideaDetail.direction)

        const tradeReasonsArray = ideaDetail.tradeReason
          .split(',')
          .map((reason) => reason.trim())
        setSelectedTradeReasons(tradeReasonsArray)
      }

      if (ideaDetail && isShortableData && fillStrategyData) {
        if (!isShortableData.data.isShortable) {
          if (fillStrategyData.data.hasUnfilledAction) {
            setMaxTargetMarketValue(ideaDetail.targetMarketValue)
          } else {
            setMaxTargetMarketValue(ideaDetail.currentMarketValue)
          }
        }
      }
    }, [fetchTickerPrice, fetchTickerSearch, ideaDetail, isShortableData])

    useEffect(() => {
      if (mode === 'create' && userInfo.region === 'Global' && buyingPower) {
        setMaxTargetMarketValue(buyingPower)
      }
    }, [mode, userInfo.region, buyingPower])

    // Update ticker options when API data changes
    useEffect(() => {
      if (tickerData?.data?.tickers) {
        setTickerOptions(
          tickerData.data.tickers.map((t) => ({
            key: t.figi,
            value: t.figi,
            label: `${t.tickerCode} - ${t.name}`,
            bbgCompositeKey: t.bbgCompositeKey,
            name: t.name,
            legalName: t.legalName,
            status: t.status,
          }))
        )
      }
    }, [tickerData])

    // Move success, updateSuccess, errorNotification inside useEffect or wrap with useCallback
    const success = useCallback(() => {
      messageApi.open({
        type: 'success',
        content: t('idea_list.idea_modal.action_idea_form.success_message'),
      })
    }, [messageApi, t])

    const updateSuccess = useCallback(() => {
      messageApi.open({
        type: 'success',
        content: t('idea_list.idea_modal.action_idea_form.update_message'),
      })
    }, [t, messageApi])

    const errorNotification = useCallback(
      (description: string) => {
        notificationApi.error({
          message: 'Error',
          description,
          placement: 'topRight',
        })
      },
      [notificationApi]
    )

    const onCloseForm = useCallback(() => {
      form.resetFields()
      setHorizonValue('')
      setConvictionValue('')
      setSelectedFigi('')
      setCompanyName('')
      onCancel()
    }, [form, onCancel])

    useEffect(() => {
      if (isSuccess) {
        success()
        onCloseForm()
        reset()
        if (onIdeaActioned) onIdeaActioned() // call after success
      }

      if (isUpdateSuccess) {
        updateSuccess()
        onCloseForm()
        updateReset()
        if (onIdeaActioned) onIdeaActioned()
      }
    }, [
      isSuccess,
      form,
      onCancel,
      reset,
      onIdeaActioned,
      isUpdateSuccess,
      updateReset,
      success,
      onCloseForm,
      updateSuccess,
    ])

    useEffect(() => {
      if (isError) {
        const description = getErrorDescription(error)
        errorNotification(description)
        reset()
      }

      if (isUpdateError) {
        const description = getErrorDescription(updateError)
        errorNotification(description)
        updateReset()
      }
    }, [
      isError,
      error,
      reset,
      isUpdateError,
      updateError,
      updateReset,
      errorNotification,
    ])

    useEffect(() => {
      if (
        checkIdeaExistData &&
        checkIdeaExistData.data.exist &&
        checkIdeaExistData.data.ideaId
      ) {
        if (onEditIdea) {
          onEditIdea(checkIdeaExistData.data.ideaId)
        }
      }
    }, [checkIdeaExistData, checkIdeaExistLoading, onEditIdea])

    // Debounced search handler
    const handleTickerSearch = debounce((value: string) => {
      if (value && value.length >= 3) {
        fetchTickerSearch({ keyword: value })
      } else {
        setTickerOptions([])
      }
    }, 400)

    const handleDirectionChange = (val: 'Long' | 'Short') => {
      setTimeout(() => setVisualDirection(val), THUMB_TRANSITION_MS)
    }

    const getMaxUpSize = (
      mode: 'create' | 'edit',
      selectedBbgCompositeKey: string,
      tickerData?: TickerSearchResponse,
      ideaDetail?: IdeaDetail
    ): number | undefined => {
      if (!tickerData) return
      const maxUpSizeString =
        mode === 'create' && tickerData
          ? tickerData.data.tickers.find(
              (x: Ticker) => x.bbgCompositeKey === selectedBbgCompositeKey
            )?.maxUpSize
          : ideaDetail?.ticker?.maxUpSize

      return maxUpSizeString ? parseFloat(maxUpSizeString) : undefined
    }

    const handleTargetMarketValueBp = (percentage: number) => () => {
      if (!buyingPower) return

      const calculatedValue = Math.round(buyingPower * percentage)

      const maxUpSize = getMaxUpSize(
        mode,
        selectedBbgCompositeKey,
        tickerData,
        ideaDetail
      )
      const previousMarketValue = ideaDetail?.targetMarketValue ?? 0

      if (!calculatedValue || !maxUpSize) return false

      if (previousMarketValue != 0) {
        if (calculatedValue - previousMarketValue > maxUpSize) {
          setIsMaxFillReached(true)
        } else {
          setIsMaxFillReached(false)
        }
      } else if (
        userInfo.region !== 'Global' &&
        maxUpSize &&
        calculatedValue &&
        calculatedValue > maxUpSize
      ) {
        setIsMaxFillReached(true)
      } else {
        setIsMaxFillReached(false)
      }

      form.setFieldsValue({ targetMarketValue: calculatedValue })
    }

    const handleTargetPrice = (percentage: number) => () => {
      const price = tickerPriceData?.data?.price
      if (!price) return

      const direction = form.getFieldValue('direction')
      const multiplier = direction === 'Short' ? 1 - percentage : 1 + percentage
      form.setFieldsValue({
        target: parseFloat((price * multiplier).toFixed(2)),
      })
    }

    const handleStopPrice = (percentage: number) => () => {
      const price = tickerPriceData?.data?.price
      if (!price) return

      const direction = form.getFieldValue('direction')
      const multiplier = direction === 'Short' ? 1 + percentage : 1 - percentage
      form.setFieldsValue({
        stop: parseFloat((price * multiplier).toFixed(2)),
      })
    }

    const handleHorizonClick = (value: string) => {
      setHorizonValue(value)
      form.setFieldsValue({ horizon: value })
    }

    const handleConvictionClick = (value: string) => {
      setConvictionValue(value)
      form.setFieldsValue({ conviction: value })
    }

    // Set company name when ticker is selected and fetch ticker price
    const handleTickerSelect = (value: string) => {
      form.resetFields(['targetMarketValue', 'direction', 'target', 'stop'])
      const selected = tickerOptions.find((t) => t.value === value)
      let finalCompanyName = selected?.name ?? ''
      if (finalCompanyName && selected?.legalName) {
        finalCompanyName = finalCompanyName.concat(
          ' (' + selected?.legalName + ')'
        )
      }
      setCompanyName(finalCompanyName)
      if (selected?.value) {
        setSelectedFigi(selected.value)
      }
      if (selected?.bbgCompositeKey) {
        fetchTickerPrice({ bbgCompositeKey: selected.bbgCompositeKey })
        setSelectedBbgCompositeKey(selected?.bbgCompositeKey)
      }

      if (userInfo.region !== 'Global' && buyingPower) {
        const maxUpSize =
          tickerData?.data?.tickers?.find(
            (t) => selected?.bbgCompositeKey === t.bbgCompositeKey
          )?.maxUpSize ?? maxTargetMarketValue

        const targetMarketValue = form.getFieldValue('targetMarketValue')

        if (maxUpSize && targetMarketValue > maxUpSize) {
          setMaxTargetMarketValue(maxUpSize as number)
          setIsMaxFillReached(true)
          form.setFieldsValue({ targetMarketValue: maxUpSize as number })
        } else {
          setIsMaxFillReached(false)
        }
      }
    }

    const onFinish: FormProps<CreateIdeaFormFieldTypes>['onFinish'] = (
      values
    ) => {
      createIdea({
        ...values,
        tradeReason: Array.isArray(values.tradeReason)
          ? values.tradeReason.join(',')
          : values.tradeReason,
      })
    }

    const onUpdate: FormProps<CreateIdeaFormFieldTypes>['onFinish'] = (
      values
    ) => {
      updateIdea({
        id: ideaDetail?.id ?? '',
        targetMarketValue: values.targetMarketValue,
        target: values.target,
        stop: values.stop,
        conviction: values.conviction,
        horizon: values.horizon,
        tradeReason: Array.isArray(values.tradeReason)
          ? values.tradeReason.join(',')
          : values.tradeReason,
        fillStrategy: values.fillStrategy,
      })
    }

    // Expose resetFormState to parent via ref
    useImperativeHandle(ref, () => ({
      resetFormState: () => {
        setHorizonValue('')
        setConvictionValue('')
        setSelectedFigi('')
        setCompanyName('')
      },
    }))

    const isMobile = useIsMobile(768)

    return (
      <>
        {messageContextHolder}
        {notificationContextHolder}
        <Form
          form={form}
          layout={'vertical'}
          onFinish={mode === 'create' ? onFinish : onUpdate}
          initialValues={{
            direction: 'Long',
            targetMarketValue: 1000000,
          }}
        >
          <Row
            gutter={
              isMobile
                ? [0, 0]
                : [
                    convertPxToNumber(themeStyle.marginBase),
                    convertPxToNumber(themeStyle.marginBase),
                  ]
            }
            align={isMobile ? 'stretch' : 'middle'}
            style={{
              marginBottom: isMobile ? '' : themeStyle.marginSM,
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Col
              span={isMobile ? 24 : 8}
              style={isMobile ? { marginBottom: 8 } : {}}
            >
              <Form.Item
                label={t('idea_list.idea_modal.action_idea_form.ticker')}
                name={'figi'}
                style={{ width: '100%' }}
                rules={[
                  {
                    required: true,
                    message: t(
                      'idea_list.idea_modal.action_idea_form.ticker_required'
                    ),
                  },
                  ...(mode === 'create'
                    ? [
                        {
                          validator: (_: RuleObject, value: string) => {
                            const tickerOption = tickerOptions.find(
                              (option) => option.value === value
                            )
                            if (value && tickerOption?.status === 'Inactive') {
                              return Promise.reject(
                                <InvalidTickerMessage
                                  message={t(
                                    'idea_list.idea_modal.action_idea_form.ticker_market_cap_adv_limit'
                                  )}
                                  ticker={tickerOption.label}
                                />
                              )
                            }
                            return Promise.resolve()
                          },
                        },
                      ]
                    : []),
                ]}
                help={(() => {
                  const currentValue = form.getFieldValue('figi')
                  const tickerOption = tickerOptions.find(
                    (option) => option.value === currentValue
                  )

                  // Show validation error first if ticker is inactive
                  if (currentValue && tickerOption?.status === 'Inactive') {
                    return (
                      <>
                        <InvalidTickerMessage
                          message={t(
                            'idea_list.idea_modal.action_idea_form.ticker_market_cap_adv_limit'
                          )}
                          ticker={tickerOption.label}
                        />
                      </>
                    )
                  }
                })()}
              >
                <Select
                  allowClear={mode === 'create'}
                  showSearch
                  placeholder={'Add Active Ticker'}
                  filterOption={false}
                  onSearch={mode === 'create' ? handleTickerSearch : undefined}
                  options={tickerOptions}
                  loading={tickerLoading}
                  notFoundContent={
                    tickerLoading ? 'Loading...' : 'No tickers found'
                  }
                  onSelect={mode === 'create' ? handleTickerSelect : undefined}
                  onClear={
                    mode === 'create'
                      ? () => {
                          setTickerOptions([])
                          setCompanyName('')
                        }
                      : undefined
                  }
                  disabled={mode == 'edit'}
                  dropdownStyle={
                    isMobile
                      ? { minWidth: 0, width: '90vw', maxWidth: '90vw' }
                      : {}
                  }
                  style={isMobile ? { width: '100%' } : {}}
                />
              </Form.Item>
            </Col>
            {companyName && (
              <Col style={{ marginTop: themeStyle.paddingBase }}>
                <IdeyaTypography.Text type={'secondary'}>
                  {companyName}
                </IdeyaTypography.Text>
              </Col>
            )}
          </Row>
          <Row
            style={{
              borderTop: isMobile ? '' : '1px solid rgba(0, 0, 0, 0.15)',
              borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Col
              span={isMobile ? 24 : 12}
              style={{
                padding: isMobile ? '12px 0px' : '12px 20px 12px 0px',
                borderRight: isMobile
                  ? undefined
                  : '1px solid rgba(0, 0, 0, 0.15)',
              }}
            >
              <Row
                align={'middle'}
                justify={'space-between'}
                gutter={[10, 10]}
                style={{ marginBottom: '10px' }}
              >
                <Form.Item name={'direction'} style={{ marginBottom: '0px' }}>
                  <Segmented
                    className={
                      visualDirection === 'Long'
                        ? styles.segmentedGreen
                        : styles.segmentedRed
                    }
                    options={[
                      {
                        value: 'Long',
                        label: <div>Long</div>,
                      },
                      {
                        value: 'Short',
                        label: <div>Short</div>,
                        disabled:
                          isShortableLoading ||
                          !isShortableData?.data.isShortable,
                      },
                    ]}
                    onChange={(val) =>
                      handleDirectionChange(val as 'Long' | 'Short')
                    }
                    disabled={mode === 'edit'}
                  />
                </Form.Item>
                <IdeyaTypography.Text>Unit : USD</IdeyaTypography.Text>
              </Row>
              <Row
                style={{
                  display: 'flex',
                  padding: isMobile ? 8 : 20,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  alignSelf: 'stretch',
                  backgroundColor:
                    visualDirection === 'Long'
                      ? themeStyle.green1
                      : themeStyle.red1,
                  gap: '20px',
                }}
              >
                <div style={{ width: '100%' }}>
                  <Form.Item
                    className={'target-market-label-item'}
                    name={'targetMarketValue'}
                    style={{ width: '100%', marginBottom: '8px' }}
                    label={
                      <div
                        className={'custom-label'}
                        style={{
                          display: isMobile ? '' : 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <IdeyaTypography.Text
                          style={{ marginRight: isMobile ? '8px' : '' }}
                        >
                          {t(
                            'idea_list.idea_modal.action_idea_form.target_market_value'
                          )}
                        </IdeyaTypography.Text>
                        <IdeyaTypography.Text style={{ color: '#EB855D' }}>
                          {isMobile ? 'Available' : 'Available Capital'} :{' '}
                          {availableCapital}
                        </IdeyaTypography.Text>
                      </div>
                    }
                    help={
                      isMaxFillReached ? (
                        <span style={{ color: '#ff4d4f' }}>
                          Maxfill will be $
                          {(
                            getMaxUpSize(
                              mode,
                              selectedBbgCompositeKey,
                              tickerData,
                              ideaDetail
                            ) ?? 0
                          ).toLocaleString()}
                        </span>
                      ) : null
                    }
                    rules={[
                      {
                        required: true,
                        message: t(
                          'idea_list.idea_modal.action_idea_form.target_market_value_required'
                        ),
                      },
                      ...(mode === 'edit'
                        ? [
                            {
                              validator: (_: RuleObject, value: string) => {
                                if (
                                  typeof value === 'number' &&
                                  value > maxTargetMarketValue
                                ) {
                                  return Promise.reject(
                                    `${t(
                                      'idea_list.idea_modal.action_idea_form.target_market_value_cannot_exceed_max_value'
                                    )}: ${maxTargetMarketValue.toLocaleString()}`
                                  )
                                }
                                return Promise.resolve()
                              },
                            },
                          ]
                        : []),
                    ]}
                  >
                    <InputNumber<number>
                      controls={false}
                      style={{ width: '100%' }}
                      formatter={(value) =>
                        `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={(value) =>
                        value?.replace(/\$\s?|(,*)/g, '') as unknown as number
                      }
                      precision={0}
                      onChange={(value) => {
                        const maxUpSize = getMaxUpSize(
                          mode,
                          selectedBbgCompositeKey,
                          tickerData,
                          ideaDetail
                        )
                        const previousMarketValue =
                          ideaDetail?.targetMarketValue ?? 0

                        if (!value || !maxUpSize) return false

                        if (previousMarketValue != 0) {
                          if (value - previousMarketValue > maxUpSize) {
                            setIsMaxFillReached(true)
                          } else {
                            setIsMaxFillReached(false)
                          }
                        } else if (
                          userInfo.region !== 'Global' &&
                          maxUpSize &&
                          value &&
                          value > maxUpSize
                        ) {
                          setIsMaxFillReached(true)
                        } else {
                          setIsMaxFillReached(false)
                        }
                      }}
                      max={maxTargetMarketValue}
                      min={MinTargetMarketValue}
                      step={TargetMarketValueStep}
                    />
                  </Form.Item>
                  <div
                    style={{
                      width: '100%',
                    }}
                  >
                    <Row
                      gutter={[
                        convertPxToNumber(themeStyle.marginXS),
                        convertPxToNumber(themeStyle.marginXS),
                      ]}
                      justify={isMobile ? 'space-between' : undefined}
                      style={{
                        flexWrap: 'wrap',
                        width: '100%',
                      }}
                    >
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetMarketValueBp(
                            isJpOrGlobal ? 0.01 : 0.02
                          )}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          {isJpOrGlobal ? '1' : '2'}% BP
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetMarketValueBp(
                            isJpOrGlobal ? 0.02 : 0.04
                          )}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          {isJpOrGlobal ? '2' : '4'}% BP
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetMarketValueBp(
                            isJpOrGlobal ? 0.03 : 0.06
                          )}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          {isJpOrGlobal ? '3' : '6'}% BP
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetMarketValueBp(
                            isJpOrGlobal ? 0.04 : 0.08
                          )}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          {isJpOrGlobal ? '4' : '8'}% BP
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetMarketValueBp(
                            isJpOrGlobal ? 0.05 : 0.1
                          )}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          {isJpOrGlobal ? '5' : '10'}% BP
                        </IdeyaButton>
                      </Col>
                    </Row>
                  </div>
                </div>
                <div style={{ width: '100%' }}>
                  <Form.Item
                    name={'target'}
                    style={{ width: '100%', marginBottom: '8px' }}
                    label={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <IdeyaTypography.Text>
                          {t(
                            'idea_list.idea_modal.action_idea_form.target_price'
                          )}
                        </IdeyaTypography.Text>
                      </div>
                    }
                    rules={[
                      {
                        required: true,
                        message: t(
                          'idea_list.idea_modal.action_idea_form.target_price_required'
                        ),
                      },
                      {
                        validator: (_, value) => {
                          const direction = form.getFieldValue('direction')
                          if (
                            direction === 'Short' &&
                            tickerPriceData &&
                            value > tickerPriceData.data.price
                          ) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'idea_list.idea_modal.action_idea_form.price_above'
                                )
                              )
                            )
                          } else if (
                            direction === 'Long' &&
                            tickerPriceData &&
                            value < tickerPriceData.data.price
                          ) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'idea_list.idea_modal.action_idea_form.price_below'
                                )
                              )
                            )
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <InputNumber<number>
                      min={0}
                      controls={false}
                      precision={2}
                      style={{ width: '100%' }}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={(value) =>
                        value?.replace(/\$\s?|(,*)/g, '') as unknown as number
                      }
                    />
                  </Form.Item>
                  <div style={{ width: '100%' }}>
                    <Row
                      gutter={[
                        convertPxToNumber(themeStyle.marginXS),
                        convertPxToNumber(themeStyle.marginXS),
                      ]}
                      justify={isMobile ? 'space-between' : undefined}
                      style={{ flexWrap: 'wrap', width: '100%' }}
                    >
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetPrice(0.1)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          10%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetPrice(0.2)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          20%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetPrice(0.3)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          30%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetPrice(0.4)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          40%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleTargetPrice(0.5)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          50%
                        </IdeyaButton>
                      </Col>
                    </Row>
                  </div>
                </div>
                <div style={{ width: '100%' }}>
                  <Form.Item
                    name={'stop'}
                    style={{ width: '100%', marginBottom: '8px' }}
                    label={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <IdeyaTypography.Text>
                          {t(
                            'idea_list.idea_modal.action_idea_form.stop_price'
                          )}
                        </IdeyaTypography.Text>
                      </div>
                    }
                    rules={[
                      {
                        required: true,
                        message: t(
                          'idea_list.idea_modal.action_idea_form.stop_price_required'
                        ),
                      },
                      {
                        validator: (_, value) => {
                          const direction = form.getFieldValue('direction')
                          if (
                            direction === 'Short' &&
                            tickerPriceData &&
                            value < tickerPriceData.data.price
                          ) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'idea_list.idea_modal.action_idea_form.price_below'
                                )
                              )
                            )
                          } else if (
                            direction === 'Long' &&
                            tickerPriceData &&
                            value > tickerPriceData.data.price
                          ) {
                            return Promise.reject(
                              new Error(
                                t(
                                  'idea_list.idea_modal.action_idea_form.price_above'
                                )
                              )
                            )
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <InputNumber<number>
                      controls={false}
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={(value) =>
                        value?.replace(/\$\s?|(,*)/g, '') as unknown as number
                      }
                    />
                  </Form.Item>
                  <div style={{ width: '100%' }}>
                    <Row
                      gutter={[
                        convertPxToNumber(themeStyle.marginXS),
                        convertPxToNumber(themeStyle.marginXS),
                      ]}
                      justify={isMobile ? 'space-between' : undefined}
                      style={{ flexWrap: 'wrap', width: '100%' }}
                    >
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleStopPrice(0.05)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          5%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleStopPrice(0.1)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          10%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleStopPrice(0.15)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          15%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleStopPrice(0.2)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          20%
                        </IdeyaButton>
                      </Col>
                      <Col>
                        <IdeyaButton
                          ideyaType={'antd'}
                          onClick={handleStopPrice(0.3)}
                          style={{ width: isMobile ? '50px' : '' }}
                        >
                          30%
                        </IdeyaButton>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Row>
            </Col>
            <Col
              span={isMobile ? 24 : 12}
              style={{
                width: '100%',
                padding: isMobile ? '12px 0px' : '12px 0px 12px 20px',
              }}
            >
              <Row
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  alignSelf: 'stretch',
                  gap: '20px',
                }}
              >
                <Form.Item
                  name={'fillStrategy'}
                  label={t(
                    'idea_list.idea_modal.action_idea_form.fill_strategy'
                  )}
                  style={{ width: '100%', marginBottom: '0px' }}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'idea_list.idea_modal.action_idea_form.fill_strategy_required'
                      ),
                    },
                  ]}
                >
                  <Radio.Group
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    disabled={
                      fillStrategyLoading ||
                      (mode === 'edit' &&
                        fillStrategyData?.data.hasUnfilledAction &&
                        fillStrategyData?.data.canFill)
                    }
                  >
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.fillStrategy !== currentValues.fillStrategy
                      }
                    >
                      {({ getFieldValue }) => (
                        <div
                          style={{
                            backgroundColor:
                              getFieldValue('fillStrategy') ===
                              FillStrategies[0].value
                                ? '#fffbe6'
                                : 'transparent',
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: themeStyle.paddingXS,
                            borderRadius: themeStyle.borderRadiusBase,
                          }}
                        >
                          <Radio value={FillStrategies[0].value}>
                            <IdeyaTypography.Text
                              style={{
                                color:
                                  theme === Theme.Dark &&
                                  getFieldValue('fillStrategy') ===
                                    FillStrategies[0].value
                                    ? '#311518'
                                    : '',
                              }}
                            >
                              {FillStrategies[0].label}
                            </IdeyaTypography.Text>
                            {getFieldValue('fillStrategy') ===
                              FillStrategies[0].value && (
                              <>
                                {' '}
                                <IdeyaTypography.Text type={'danger'}>
                                  ⚠️ Orders will be completed within 15 minutes
                                  — this cannot be reversed once submitted.
                                </IdeyaTypography.Text>
                              </>
                            )}
                          </Radio>
                        </div>
                      )}
                    </Form.Item>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        borderRadius: themeStyle.borderRadiusBase,
                      }}
                    >
                      <Radio value={FillStrategies[1].value}>
                        {FillStrategies[1].label}
                      </Radio>
                    </div>
                  </Radio.Group>
                </Form.Item>
                {!isMobile && (
                  <Form.Item
                    name={'tradeReason'}
                    label={t(
                      'idea_list.idea_modal.action_idea_form.trade_reason'
                    )}
                    style={{ width: '100%', marginBottom: '0px' }}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'idea_list.idea_modal.action_idea_form.trade_reason_required'
                        ),
                      },
                    ]}
                  >
                    <TreeSelect
                      showSearch
                      style={{ width: '100%' }}
                      placeholder={'Select trade reason'}
                      allowClear
                      multiple
                      treeDefaultExpandAll
                      treeData={TradeReasonsTreeData}
                      treeCheckable={true}
                      listHeight={isMobile ? 300 : 500}
                      dropdownStyle={
                        isMobile
                          ? { minWidth: 0, width: '90vw', maxWidth: '90vw' }
                          : {}
                      }
                    />
                  </Form.Item>
                )}
                <Form.Item
                  name={'horizon'}
                  label={t('idea_list.idea_modal.action_idea_form.horizon')}
                  style={{ width: '100%', marginBottom: '0px' }}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'idea_list.idea_modal.action_idea_form.horizon_required'
                      ),
                    },
                  ]}
                >
                  <div style={{ width: '100%' }}>
                    <Row
                      gutter={[
                        convertPxToNumber(themeStyle.marginXS),
                        convertPxToNumber(themeStyle.marginXS),
                      ]}
                      style={{ flexWrap: 'wrap', width: '100%' }}
                    >
                      {[
                        'Intraday',
                        '1-4 Days',
                        '1-2 Weeks',
                        '2-4 Weeks',
                        '1-3 Months',
                        'Quarter',
                      ].map((value) => (
                        <Col key={value}>
                          <IdeyaButton
                            ideyaType={horizonValue === value ? '' : 'antd'}
                            onClick={() => handleHorizonClick(value)}
                            style={{
                              justifyContent: 'center',
                              display: 'flex',
                              borderColor: themeStyle.colorSplit,
                              width: isMobile ? '150px' : '',
                            }}
                          >
                            {value}
                          </IdeyaButton>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Form.Item>
                <Form.Item
                  name={'conviction'}
                  label={t('idea_list.idea_modal.action_idea_form.conviction')}
                  style={{ width: '100%', marginBottom: '0px' }}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'idea_list.idea_modal.action_idea_form.conviction_required'
                      ),
                    },
                  ]}
                >
                  <div style={{ width: '100%' }}>
                    <Row
                      gutter={[
                        convertPxToNumber(themeStyle.marginXS),
                        convertPxToNumber(themeStyle.marginXS),
                      ]}
                      style={{ flexWrap: 'wrap', width: '100%' }}
                    >
                      {['Very high', 'High', 'Medium', 'Low'].map((value) => (
                        <Col key={value}>
                          <IdeyaButton
                            ideyaType={convictionValue === value ? '' : 'antd'}
                            onClick={() => handleConvictionClick(value)}
                            style={{
                              justifyContent: 'center',
                              display: 'flex',
                              borderColor: themeStyle.colorSplit,
                              width: isMobile ? '150px' : '',
                            }}
                          >
                            {value}
                          </IdeyaButton>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Form.Item>
                {isMobile && (
                  <>
                    <Form.Item
                      name={'tradeReason'}
                      label={t(
                        'idea_list.idea_modal.action_idea_form.trade_reason'
                      )}
                      style={{ width: '100%', marginBottom: '0px' }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'idea_list.idea_modal.action_idea_form.trade_reason_required'
                          ),
                        },
                      ]}
                    >
                      <Checkbox.Group
                        style={{ width: '100%' }}
                        onChange={(checkedValues) =>
                          setSelectedTradeReasons(checkedValues as string[])
                        }
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                          }}
                        >
                          {TradeReasonsTreeData.map((category) => (
                            <div key={category.value}>
                              {category.children ? (
                                <>
                                  <IdeyaTypography.Text type={'secondary'}>
                                    {category.title}
                                  </IdeyaTypography.Text>
                                  {category.children.map((child) => (
                                    <div
                                      style={{
                                        borderColor:
                                          selectedTradeReasons.includes(
                                            child.value
                                          )
                                            ? themeStyle.colorSplit
                                            : themeStyle.colorBorder,
                                        borderRadius: `${themeStyle.borderRadiusBase}`,
                                        border: `1px solid ${themeStyle.colorBorder}`,
                                        padding: `6px 15px`,
                                        marginBottom: '8px',
                                        backgroundColor:
                                          selectedTradeReasons.includes(
                                            child.value
                                          )
                                            ? themeStyle.blue6
                                            : '',
                                      }}
                                    >
                                      <Checkbox
                                        key={child.value}
                                        value={child.value}
                                        style={{
                                          color: selectedTradeReasons.includes(
                                            child.value
                                          )
                                            ? themeStyle.blue9
                                            : themeStyle.colorText,
                                        }}
                                      >
                                        {child.title}
                                      </Checkbox>
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <div
                                  style={{
                                    borderColor: selectedTradeReasons.includes(
                                      category.value
                                    )
                                      ? themeStyle.colorSplit
                                      : themeStyle.colorBorder,
                                    borderRadius: `${themeStyle.borderRadiusBase}`,
                                    border: `1px solid ${themeStyle.colorBorder}`,
                                    padding: `6px 15px`,
                                    backgroundColor:
                                      selectedTradeReasons.includes(
                                        category.value
                                      )
                                        ? themeStyle.blue6
                                        : '',
                                  }}
                                >
                                  <Checkbox
                                    value={category.value}
                                    style={{
                                      color: selectedTradeReasons.includes(
                                        category.value
                                      )
                                        ? themeStyle.blue9
                                        : themeStyle.colorText,
                                    }}
                                  >
                                    {category.title}
                                  </Checkbox>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </Checkbox.Group>
                    </Form.Item>
                  </>
                )}
                <Form.Item
                  label={t('idea_list.idea_modal.action_idea_form.comment')}
                  name={'comment'}
                  style={{ width: '100%' }}
                >
                  <Input.TextArea
                    rows={isMobile ? 3 : 1}
                    style={{ width: '100%' }}
                    placeholder={t(
                      'idea_list.idea_modal.action_idea_form.add_comment'
                    )}
                  />
                </Form.Item>
              </Row>
            </Col>
          </Row>
          <Row
            justify={'space-between'}
            style={{
              padding: isMobile ? '12px 0px 0px 0px' : `12px 0px 0px 0px`,
              flexDirection: 'row',
              gap: isMobile ? 8 : 0,
            }}
          >
            <Col style={isMobile ? { marginBottom: 8 } : {}}>
              <IdeyaButton
                type={'text'}
                onClick={() => {
                  form.resetFields()
                  setHorizonValue('')
                  setConvictionValue('')
                  setCompanyName('')
                  setIsMaxFillReached(false)
                  setVisualDirection('Long')
                }}
                disabled={mode === 'edit'}
                style={{
                  color: themeStyle.colorPrimaryText,
                }}
              >
                {t('idea_list.idea_modal.action_idea_form.clear_form')}
              </IdeyaButton>
            </Col>
            <Col>
              <Row gutter={[8, 8]}>
                {mode === 'create' ? (
                  <>
                    <Col>
                      <IdeyaButton
                        ideyaType={'secondary'}
                        onClick={onCloseForm}
                      >
                        {t('common.cancel')}
                      </IdeyaButton>
                    </Col>
                    <Col>
                      <IdeyaButton loading={isCreating} htmlType={'submit'}>
                        {t('common.create_idea')}
                      </IdeyaButton>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col>
                      <IdeyaButton
                        type={'primary'}
                        danger
                        onClick={onIdeaLiquidated}
                      >
                        {t('idea_list.idea_modal.action_idea_form.liquidate')}
                      </IdeyaButton>
                    </Col>
                    <Col>
                      <IdeyaButton
                        onClick={form.submit}
                        loading={isUpdateLoading}
                      >
                        {t('idea_list.idea_modal.action_idea_form.update')}
                      </IdeyaButton>
                    </Col>
                  </>
                )}
              </Row>
            </Col>
          </Row>
        </Form>
      </>
    )
  }
)
